import { useDispatch, useSelector } from "react-redux";
import { setConsentFormOpen } from "../../store/currentPreferencesSlice";
import store from "../../store/store";
import { AlertType, UserRoles } from "../constants/enums";
import moment from "moment";
import { HtmlHelpers } from "../constants/all-constant";
import { toast } from "../../store/toastSlice";
import { DateFormatter } from "./DateFormatter";
import { AppConfigurationHelper } from "../../services/AppConfigurationHelper";

export class CommonFunctions {
  static openConsentForm() {
    store.dispatch(setConsentFormOpen(true));
  }

  static closeConsentForm() {
    store.dispatch(setConsentFormOpen(false));
  }

  static nameFormats = {
    doctor: "Dr.",
    sn: "SN",
    rn: "RN",
  };

  //using
  // Only prefix Ex: Dr.John => CommonFunctions.nameFormats("John", {prefixKey:'doctor'})
  // Only suffix Ex: John (RN) => CommonFunctions.nameFormats("John", {suffixKey:'rn'})
  // both Ex: Dr.John(SN) => CommonFunctions.nameFormats("John", {prefixKey:'doctor', suffixKey:'sn'})

  static formatName(
    name: string,
    options?: {
      prefixKey?: keyof typeof CommonFunctions.nameFormats;
      suffixKey?: keyof typeof CommonFunctions.nameFormats;
    }
  ): string {
    if (!name) return "NA";
    const prefix = options?.prefixKey
      ? `${this.nameFormats[options.prefixKey]}`
      : "";
    const suffix = options?.suffixKey
      ? `(${this.nameFormats[options.suffixKey]})`
      : "";
    return `${prefix} ${name} ${suffix}`;
  }

  static formatFirstAndLastName(
    firstName: string,
    lastName: string,
    options?: {
      prefixKey?: keyof typeof CommonFunctions.nameFormats;
      suffixKey?: keyof typeof CommonFunctions.nameFormats;
    }
  ): string {
    if (!firstName || !lastName) return "NA";
    const fullName = `${firstName?.charAt(0)?.toUpperCase()} ${lastName}`;
    if (options?.prefixKey) {
      return `${this.nameFormats[options.prefixKey]} ${fullName}`;
    } else if (options?.suffixKey) {
      return `${fullName} (${this.nameFormats[options.suffixKey]})`;
    }
    return `${fullName}`;
  }

  static IsCurrentUserStudent = () => {
    const auth = useSelector((state: any) => state.login);
    return auth.userInfo.roleId === UserRoles.NURSING_STUDENT;
  };

  static IsCurrentUserTutor = () => {
    const auth = useSelector((state: any) => state.login);
    return auth.userInfo.roleId === UserRoles.TUTOR;
  };

  static IsCurrentSuperUser = () => {
    const auth = useSelector((state: any) => state.login);
    return auth?.userInfo?.roleId === UserRoles.SUPER_USER;
  };

  static IsCurrentUserSubCoordinator = () => {
    const auth = useSelector((state: any) => state.login);
    return auth?.userInfo?.roleId === UserRoles.SUBJECT_COORDINATOR;
  };

  static AddZerosToNumber = (num: number, size: number) => {
    if (!num) return "";
    let stringNumber = num.toString();
    while (stringNumber.length < size) stringNumber = "0" + stringNumber;
    return stringNumber;
  };

  static ConvertIdToDisplayId = (
    id: number,
    type: "template" | "case"
  ): string => {
    let IDLength = type === "case" ? 6 : 3;
    return (type === "case" ? "C" : "T") + this.AddZerosToNumber(id, IDLength);
  };

  static convertOffsetToMinutes = (inputOffset: string): number => {
    let isNegative;
    if (inputOffset.startsWith("-")) {
      isNegative = true;
      inputOffset = inputOffset.substring(1);
    }
    const splittedValues = inputOffset.split(" ");
    const minutesValues = splittedValues.map((x) => {
      if (x.endsWith("D") || x.endsWith("d")) {
        return Number(x.replace("D", "").replace("d", "")) * 24 * 60;
      } else if (x.endsWith("H") || x.endsWith("h")) {
        return Number(x.replace("H", "").replace("h", "")) * 60;
      } else if (x.endsWith("M") || x.endsWith("m")) {
        return Number(x.replace("M", "").replace("m", ""));
      }
      return 0;
    });
    const sum = minutesValues.reduce((partialSum, a) => partialSum + a, 0);
    return isNegative ? -1 * sum : sum;
  };

  static convertMinutesToOffset = (
    valueInMinutes: number | undefined
  ): string => {
    if (valueInMinutes === undefined) return "";
    if (valueInMinutes === 0) return "0";
    let positiveNumber = valueInMinutes;
    if (positiveNumber < 0) {
      positiveNumber = positiveNumber * -1;
    }
    let result =
      (moment.duration(positiveNumber, "minutes").asDays() >= 1
        ? Math.floor(moment.duration(positiveNumber, "minutes").asDays()) +
          "D " +
          HtmlHelpers.DOUBLE_SPACE
        : "") +
      (moment.duration(positiveNumber, "minutes").hours() > 0
        ? moment.duration(positiveNumber, "minutes").hours() +
          "H " +
          HtmlHelpers.DOUBLE_SPACE
        : "") +
      (moment.duration(positiveNumber, "minutes").minutes() > 0
        ? moment.duration(positiveNumber, "minutes").minutes() + "M"
        : "");
    if (valueInMinutes < 0) {
      result = "- " + result;
    }
    return result;
  };

  static convertMinutesToOffsetObject = (
    valueInMinutes: number | undefined
  ): { isNegative: boolean; day: number; hour: number; min: number } => {
    const offsetObject = { isNegative: false, day: 0, hour: 0, min: 0 };
    if (valueInMinutes === undefined) return offsetObject;
    if (valueInMinutes === 0) return offsetObject;
    let positiveNumber = valueInMinutes;
    if (positiveNumber < 0) {
      positiveNumber = positiveNumber * -1;
      offsetObject.isNegative = true;
    }
    offsetObject.day = Math.floor(
      moment.duration(positiveNumber, "minutes").asDays()
    );
    offsetObject.hour = moment.duration(positiveNumber, "minutes").hours();
    offsetObject.min = moment.duration(positiveNumber, "minutes").minutes();

    return offsetObject;
  };

  static convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve((reader.result as string)?.split(",")[1]);
      reader.onerror = reject;
    });
  };

  static truncateText = (text: string, maxLength: number) =>
    text && text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;

  static importCSVData = (
    file: File,
    headers: string[] = []
  ): Promise<any[] | string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const fileContent = e.target.result;
          const rows = fileContent
            .trim()
            .split("\n")
            .map((row: string) => row.trim());
          const parsedData = rows.map((row: string) => row.split(","));
          const getHeaders = parsedData[0];
          const isValidHeader = headers.every((header) =>
            getHeaders.includes(header)
          );
          if (!isValidHeader) {
            resolve("InvalidFormat");
          }
          const dataObjects = parsedData.slice(1).map((row: string[]) => {
            const rowData: Record<string, string> = {};
            row.forEach((col: string, idx: number) => {
              rowData[headers[idx]] = col;
            });
            return rowData;
          });
          resolve(dataObjects);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  };

  static donwloadFileFromBlob = (result: any = [], fileName: string) => {
    const csv = this.convertToCSV(result);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const name = `${fileName.split(".")[0]}_${DateFormatter.formatDate(
      new Date().toISOString()
    )}.csv`;

    this.downloadFileFromLink(url, name);
    URL.revokeObjectURL(url);
  };

  static convertToCSV = (arrData: any) => {
    const header = Object.keys(arrData[0]).join(",");
    const rows = arrData
      .map((obj: any) => Object.values(obj).join(","))
      .join("\n");
    return `${header}\n${rows}`;
  };

  static downloadFileFromLink = (url: any, fileName: string) => {
    let link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("target", "_blank");
    link.setAttribute("download", fileName);
    link.click();
  };

  static message = (message: string, type: AlertType) => {
    store.dispatch(
      toast({
        isOpen: true,
        message: message,
        type: type,
      })
    );
  };

  static ConvertUiSchemaToReadOnly = (
    uiSchema: string,
    dataJson: string,
    isNeurovascular: boolean
  ): { ui: {}; data: {} } => {
    const uiInput = JSON.parse(uiSchema);
    const dataInput = JSON.parse(dataJson);
    let result = { ui: {}, data: {} };
    if (Object.keys(uiInput).length > 0) {
      result = this.UpdateControlsToReadOnlyRecursive(
        uiInput,
        dataInput,
        isNeurovascular
      );
    } else {
      result = { data: {}, ui: {} };
    }
    return result;
  };

  static UpdateControlsToReadOnlyRecursive = (
    uiSchema: any,
    dataInput: any,
    isNeurovascular: boolean
  ): { ui: any; data: any } => {
    const hasTypeProp = Object.keys(uiSchema).some((x) => x === "type");
    if (hasTypeProp) {
      if (uiSchema.type === "Control") {
        if (uiSchema.options) uiSchema.options.readonly = true;
        else {
          uiSchema.options = {};
          uiSchema.options.readonly = true;
        }
      } else if (Array.isArray(uiSchema.elements)) {
        uiSchema.elements.forEach((item: any, index: number) => {
          return this.UpdateControlsToReadOnlyRecursive(
            item,
            dataInput,
            isNeurovascular
          );
        });
      } else if (uiSchema.type === "ListWithDetail") {
        if (isNeurovascular) {
          dataInput.neurovascularItems.forEach((item: any, index: number) => {
            item.readonly = true;
          });
        }
      }
    }
    return { ui: uiSchema, data: dataInput };
  };

  static clearLocalStorage = () => {
    while (localStorage.length > 0) {
      const key = localStorage.key(0);
      localStorage.removeItem(key || "");
    }
    while (sessionStorage.length > 0) {
      const key = sessionStorage.key(0);
      sessionStorage.removeItem(key || "");
    }
  };

  static getDisplayValue = (value: any): string => {
    return value === null || value === undefined || value === ""
      ? AppConfigurationHelper.Instance().displayDefaults?.emptyFallback
      : value;
  };
}
